
import { supabase } from "@/integrations/supabase/client";

export const useStripeSubscriptionCreation = () => {
  const createStripeSubscription = async (paymentMethod: any, isYearly?: boolean, promotionCode?: string, countryCode?: string) => {
    console.log("Attempting subscription with payment method ID:", paymentMethod.id);
    console.log("Payment method type:", paymentMethod.type);
    console.log("Is yearly subscription:", isYearly);
    console.log("Client country code:", countryCode);
    
    if (promotionCode) {
      console.log("Promotion code provided:", promotionCode);
    }

    // Tax rate mapping for Switzerland and Liechtenstein - UPDATED TAX IDs
    const taxRateMap: Record<string, string> = {
      'CH': 'txr_1RJX36HT9Nk9Fhr3kKYleL8R',
      'LI': 'txr_1RJX3THT9Nk9Fhr3tLphVVpw'
    };

    try {
      const currentUser = await supabase.auth.getUser();
      const userEmail = currentUser.data.user?.email;
      const userId = currentUser.data.user?.id;
      
      if (!userId) {
        throw new Error("User not logged in");
      }
      
      console.log("User authenticated:", userId);
      console.log("User email:", userEmail);
      console.log("About to call create-subscription edge function");
      
      // Determine tax rate ID if applicable
      const defaultTaxRateId = countryCode && taxRateMap[countryCode] ? taxRateMap[countryCode] : null;
      
      if (defaultTaxRateId) {
        console.log(`Applying tax rate for ${countryCode}:`, defaultTaxRateId);
      }
      
      const { data, error } = await supabase.functions.invoke('create-subscription', {
        body: {
          paymentMethodId: paymentMethod.id,
          isYearly: isYearly,
          customerEmail: userEmail,
          userId: userId,
          promotionCode: promotionCode || null,
          defaultTaxRateId: defaultTaxRateId
        }
      });

      console.log("Edge function response received:", data ? "Data present" : "No data");
      if (error) {
        console.error("Edge function error:", error);
        
        // More descriptive error based on error type, avoiding technical details
        if (error.message.includes("network")) {
          throw new Error("Connection error: Please check your internet connection and try again");
        } else {
          // Log the technical error but don't show it to the user
          console.error("Technical error details:", error.message);
          throw new Error("There was a problem processing your payment. Please try again.");
        }
      }

      if (!data) {
        console.error("No response data from edge function");
        throw new Error("There was a problem processing your payment. Please try again later.");
      }

      console.log("Subscription response:", data);
      
      if (data.error) {
        console.error("Subscription error from response:", data.error);
        
        // Map common Stripe errors to user-friendly messages
        if (data.error.includes("card_declined") || data.error.includes("insufficient_funds")) {
          throw new Error("Your card was declined. Please check your card details or try a different payment method.");
        } else if (data.error.includes("expired_card")) {
          throw new Error("Your card has expired. Please use a different card.");
        } else if (data.error.includes("incorrect_cvc")) {
          throw new Error("The CVC code is incorrect. Please check the code on the back of your card.");
        } else if (data.error.includes("processing_error")) {
          throw new Error("There was an error processing your card. Please try again or use a different payment method.");
        } else if (data.error.includes("rate_limit")) {
          throw new Error("Too many requests. Please wait a moment and try again.");
        } else if (data.error.includes("promotion_code_not_found")) {
          throw new Error("The promotion code is invalid or expired. Please try a different code.");
        } else if (data.error.includes("promotion_code_expired")) {
          throw new Error("This promotion code has expired. Please try a different code.");
        } else {
          // Log the technical error but return a cleaner message
          console.error("Unhandled Stripe error:", data.error);
          throw new Error("There was a problem with your payment. Please try again or contact support.");
        }
      }

      // Add user ID to response data for referral processing
      return {
        ...data,
        userId
      };
    } catch (error: any) {
      // Log the full error for debugging
      console.error("Subscription creation error:", error);
      
      // Make sure we always return a user-friendly error message without technical details
      const errorMessage = error.message || "An unexpected error occurred. Please try again later.";
      throw new Error(errorMessage);
    }
  };

  return { createStripeSubscription };
};
